import React, { useState, useEffect } from 'react';
import { Star, Gift, Clock, TrendingUp, Award, ChevronRight, Check, Sparkles } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { getLocalizedField, pickLocalizedText } from '../utils/localizedContent';


export default function Loyalty() {
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);
  const [success, setSuccess] = useState('');
  const { user } = useAuth();
  const { t, lang } = useLang();
  const text = (es, en) => pickLocalizedText(lang, es, en);

  useEffect(() => {
    if (user) {
      Promise.all([
        axios.get(`${API}/loyalty/balance`),
        axios.get(`${API}/loyalty/history?limit=10`),
        axios.get(`${API}/loyalty/vouchers`)
      ]).then(([balRes, histRes, vouchRes]) => {
        setBalance(balRes.data);
        setHistory(histRes.data.transactions || []);
        setVouchers(vouchRes.data.vouchers || []);
      }).catch(() => {}).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleRedeem = async (optionId) => {
    setRedeeming(optionId);
    try {
      const { data } = await axios.post(`${API}/loyalty/redeem`, { option_id: optionId });
      setSuccess(t('loyalty.success'));
      // Refrescar datos
      const [balRes, vouchRes] = await Promise.all([
        axios.get(`${API}/loyalty/balance`),
        axios.get(`${API}/loyalty/vouchers`)
      ]);
      setBalance(balRes.data);
      setVouchers(vouchRes.data.vouchers || []);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setRedeeming(null);
    }
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Gift size={64} className="mx-auto mb-4 text-[#C9A962]/30" />
          <h2 className="text-2xl font-bold text-[#F5F0E6] mb-2" style={{ fontFamily: 'Cinzel, serif' }}>
            {t('loyalty.title')}
          </h2>
          <p className="text-[#A8B5AE]">{text('Inicia sesión para ver tus puntos', 'Log in to see your points')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-20 text-[#A8B5AE]">{t('common.loading')}</div>;
  }

  const level = balance?.level || { level: 'bronze', color: '#CD7F32', multiplier: 1 };
  const levelNames = { bronze: t('loyalty.bronze'), silver: t('loyalty.silver'), gold: t('loyalty.gold'), platinum: t('loyalty.platinum') };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 page-enter">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C9A962] to-[#D4B87A] flex items-center justify-center">
          <Star size={24} className="text-[#0A1612]" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-[#F5F0E6]" style={{ fontFamily: 'Cinzel, serif' }}>{t('loyalty.title')}</h1>
          <p className="text-[#A8B5AE] text-sm">{text('Gana puntos y canjea recompensas', 'Earn points and redeem rewards')}</p>
        </div>
      </div>

      {success && (
        <div className="mb-6 p-4 bg-[#2D8B70]/20 border border-[#2D8B70]/30 rounded-xl flex items-center gap-3">
          <Check size={20} className="text-[#2D8B70]" />
          <span className="text-[#2D8B70] font-medium">{success}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Balance Card */}
        <div className="lg:col-span-2 space-y-6">
          {/* Points Balance */}
          <div className="bg-gradient-to-br from-[#0D5E4A]/40 to-[#0F1F1A] border border-[#C9A962]/20 rounded-2xl p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#C9A962]/5 rounded-full blur-3xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[#A8B5AE] text-sm font-medium">{t('loyalty.your_points')}</span>
                <div className="flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: `${level.color}20`, color: level.color, border: `1px solid ${level.color}40` }}>
                  <Award size={12} />
                  {levelNames[level.level] || level.level}
                </div>
              </div>
              <div className="flex items-end gap-2 mb-2">
                <span className="text-5xl font-black text-[#C9A962]">{balance?.current_points?.toLocaleString() || 0}</span>
                <span className="text-[#A8B5AE] text-lg mb-2">{t('loyalty.points')}</span>
              </div>
              {level.next_level && (
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-[#A8B5AE] mb-1">
                    <span>{t('loyalty.next_level')}: {levelNames[level.next_level]}</span>
                    <span>{level.points_to_next} pts</span>
                  </div>
                  <div className="h-2 bg-[#0A1612] rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all" 
                      style={{ 
                        width: `${Math.min(100, ((balance?.lifetime_points || 0) / ((balance?.lifetime_points || 0) + level.points_to_next)) * 100)}%`,
                        backgroundColor: level.color 
                      }}
                    ></div>
                  </div>
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-[#C9A962]/10 flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <TrendingUp size={14} className="text-[#2D8B70]" />
                  <span className="text-[#A8B5AE]">{t('loyalty.bonus_multiplier')}: <span className="text-[#F5F0E6] font-bold">×{level.multiplier}</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles size={14} className="text-[#C9A962]" />
                  <span className="text-[#A8B5AE]">{balance?.points_per_euro} {t('loyalty.points_per_euro')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Redemption Options */}
          <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-2xl p-6">
            <h2 className="text-lg font-bold text-[#F5F0E6] mb-4 flex items-center gap-2">
              <Gift size={18} className="text-[#C9A962]" />
              {t('loyalty.redeem_points')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {balance?.redemption_options?.map((option) => {
                const canRedeem = (balance?.current_points || 0) >= option.points;
                const isDiscount = option.type === 'discount';
                return (
                  <button
                    key={option.id}
                    onClick={() => canRedeem && handleRedeem(option.id)}
                    disabled={!canRedeem || redeeming === option.id}
                    className={`p-4 rounded-xl border text-left transition-all ${canRedeem ? 'bg-[#162B24]/50 border-[#C9A962]/30 hover:border-[#C9A962] hover:bg-[#162B24]' : 'bg-[#0A1612]/50 border-[#C9A962]/10 opacity-50 cursor-not-allowed'}`}
                    data-testid={`redeem-${option.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDiscount ? 'bg-[#C9A962]/20' : 'bg-[#2D8B70]/20'}`}>
                        {isDiscount ? <Gift size={18} className="text-[#C9A962]" /> : <Clock size={18} className="text-[#2D8B70]" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-[#F5F0E6] font-semibold text-sm">{getLocalizedField(option, 'label', lang, option.label_en || option.label_es)}</p>
                        <p className="text-[#C9A962] text-xs font-bold">{option.points} pts</p>
                      </div>
                      {redeeming === option.id ? (
                        <div className="w-5 h-5 border-2 border-[#C9A962] border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <ChevronRight size={16} className="text-[#A8B5AE]" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Active Vouchers */}
          <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-2xl p-5">
            <h3 className="text-sm font-bold text-[#F5F0E6] mb-3 flex items-center gap-2">
              <Gift size={14} className="text-[#C9A962]" />
              {text('Mis Vales Activos', 'My Active Vouchers')}
            </h3>
            {vouchers.length === 0 ? (
              <p className="text-[#A8B5AE] text-xs text-center py-4">{text('Sin vales activos', 'No active vouchers')}</p>
            ) : (
              <div className="space-y-2">
                {vouchers.map(v => (
                  <div key={v.voucher_id} className="p-3 bg-[#162B24]/50 rounded-lg border border-[#C9A962]/20">
                    <div className="flex items-center justify-between">
                      <span className="text-[#F5F0E6] text-sm font-medium">
                        {v.type === 'discount' ? `${v.value}€ ${t('loyalty.discount')}` : `${v.value}h ${t('loyalty.free_hour')}`}
                      </span>
                      <span className="text-[#2D8B70] text-xs px-2 py-0.5 rounded-full bg-[#2D8B70]/20">Activo</span>
                    </div>
                    <p className="text-[#A8B5AE] text-xs mt-1">#{v.voucher_id.slice(-8)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* History */}
          <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-2xl p-5">
            <h3 className="text-sm font-bold text-[#F5F0E6] mb-3">{t('loyalty.history')}</h3>
            {history.length === 0 ? (
              <p className="text-[#A8B5AE] text-xs text-center py-4">{text('Sin actividad', 'No activity')}</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map(txn => (
                  <div key={txn.transaction_id} className="flex items-center justify-between py-2 border-b border-[#C9A962]/10 last:border-0">
                    <div>
                      <p className="text-[#F5F0E6] text-xs">{getLocalizedField(txn, 'description', lang, txn.description_en || txn.description_es)}</p>
                      <p className="text-[#A8B5AE] text-xs">{new Date(txn.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className={`text-sm font-bold ${txn.points > 0 ? 'text-[#2D8B70]' : 'text-[#C45B4A]'}`}>
                      {txn.points > 0 ? '+' : ''}{txn.points}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* How to Earn */}
          <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-2xl p-5">
            <h3 className="text-sm font-bold text-[#F5F0E6] mb-3">{t('loyalty.earn_points')}</h3>
            <div className="space-y-3 text-xs">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#C9A962]/20 flex items-center justify-center flex-shrink-0">
                  <Gift size={14} className="text-[#C9A962]" />
                </div>
                <span className="text-[#A8B5AE]">{text('10 puntos por cada euro gastado en la tienda', '10 points for every euro spent in the store')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#2D8B70]/20 flex items-center justify-center flex-shrink-0">
                  <Clock size={14} className="text-[#2D8B70]" />
                </div>
                <span className="text-[#A8B5AE]">{text('50 puntos por cada reserva', '50 points for each reservation')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#D4B87A]/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={14} className="text-[#D4B87A]" />
                </div>
                <span className="text-[#A8B5AE]">{text('Sube de nivel para ganar más puntos', 'Level up to earn more points')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
